/**
 * Bauhaus Theme
 *
 * Geometric design with primary colors and light background.
 * Inspired by Bauhaus design movement: geometric shapes, primary colors, functional minimalism.
 */

import { Theme } from '../types';
import { TextStyle, ViewStyle } from 'react-native';

export const bauhaus: Theme = {
  id: 'bauhaus',
  name: 'Bauhaus',

  colors: {
    // Primary - Bauhaus Red
    primary: '#D02020',
    primaryLight: '#F04040',
    primaryDark: '#A01818',

    // Secondary - Bauhaus Blue
    secondary: '#1040C0',
    accent: '#F0C020', // Bauhaus Yellow

    // Status colors
    success: '#2A9D8F', // Teal
    warning: '#F0C020', // Yellow
    warningBackground: '#FFF8E6', // Light yellow background
    warningBorder: '#E6B800', // Darker yellow border
    warningText: '#9A7A00', // Dark yellow text
    error: '#D02020', // Primary red
    info: '#1040C0', // Secondary blue

    // Neutral grays - light theme
    background: '#F0F0F0', // Off-white canvas
    backgroundSecondary: '#E8E8E8',
    backgroundElevated: '#FFFFFF',

    surface: '#FFFFFF',
    surfaceSecondary: '#F8F8F8',

    text: '#121212', // Stark black
    textSecondary: '#666666', // Medium gray
    textTertiary: '#999999', // Light gray
    textInverse: '#FFFFFF',

    border: '#121212', // Thick black borders
    borderLight: '#E0E0E0',
    divider: '#E0E0E0',

    // Overlays
    overlay: 'rgba(0, 0, 0, 0.6)',
    overlayLight: 'rgba(0, 0, 0, 0.3)',

    // Selected state background
    selectedBackground: '#FFE6E6', // Light pink for selected options

    // Chart colors - Bauhaus primary palette: bold geometric primaries with warm neutrals
    chartColors: [
      '#D02020', // Bauhaus Red
      '#1040C0', // Bauhaus Blue
      '#F0C020', // Bauhaus Yellow
      '#2A9D8F', // Teal
      '#E07020', // Warm Orange
      '#6A4C93', // Deep Purple
      '#4A7C59', // Forest Green
      '#C45B84', // Rose
      '#3A6EA5', // Steel Blue
      '#8B6F47', // Warm Brown
    ],
  },

  typography: {
    // Font sizes (4px base scale)
    fontSizeXs: 12,
    fontSizeSm: 14,
    fontSizeMd: 16,
    fontSizeLg: 18,
    fontSizeXl: 20,
    fontSize2Xl: 24,
    fontSize3Xl: 32,
    fontSize4Xl: 40,

    // Font weights
    fontWeightLight: 300,
    fontWeightNormal: 400,
    fontWeightMedium: 500,
    fontWeightSemibold: 600,
    fontWeightBold: 700,

    // Line heights (relative to font size)
    lineHeightTight: 1.2,
    lineHeightNormal: 1.5,
    lineHeightRelaxed: 1.75,

    // Letter spacing
    letterSpacingTight: -0.5,
    letterSpacingNormal: 0,
    letterSpacingWide: 0.5,
  },

  spacing: {
    unit: 4,
    xxs: 2,
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    '2xl': 24,
    '3xl': 32,
    '4xl': 40,
    '5xl': 48,
  },

  radii: {
    none: 0, // Sharp corners for squares
    sm: 2,
    md: 4,
    lg: 8,
    xl: 12,
    full: 9999, // Perfect circles
  },

  shadows: {
    none: {} as ViewStyle,
    sm: {
      shadowColor: '#000',
      shadowOffset: { width: 3, height: 3 },
      shadowOpacity: 1,
      shadowRadius: 0,
      elevation: 2,
    } as ViewStyle,
    md: {
      shadowColor: '#000',
      shadowOffset: { width: 6, height: 6 },
      shadowOpacity: 1,
      shadowRadius: 0,
      elevation: 4,
    } as ViewStyle,
    lg: {
      shadowColor: '#000',
      shadowOffset: { width: 8, height: 8 },
      shadowOpacity: 1,
      shadowRadius: 0,
      elevation: 6,
    } as ViewStyle,
    xl: {
      shadowColor: '#000',
      shadowOffset: { width: 12, height: 12 },
      shadowOpacity: 1,
      shadowRadius: 0,
      elevation: 8,
    } as ViewStyle,
  },

  breakpoints: {
    sm: 375,
    md: 768,
    lg: 1024,
    xl: 1280,
  },

  motion: {
    durationFast: 150,
    durationNormal: 300,
    durationSlow: 500,

    easingDefault: 'ease-out',
    easingIn: 'ease-in',
    easingOut: 'ease-out',
    easingInOut: 'ease-in-out',
  },

  presets: {
    heading1: {
      fontSize: 40,
      fontWeight: '700',
      color: '#121212',
      letterSpacing: -0.5,
      textTransform: 'uppercase',
    } as TextStyle,

    heading2: {
      fontSize: 32,
      fontWeight: '700',
      color: '#121212',
      letterSpacing: -0.5,
      textTransform: 'uppercase',
    } as TextStyle,

    heading3: {
      fontSize: 24,
      fontWeight: '600',
      color: '#121212',
      textTransform: 'uppercase',
    } as TextStyle,

    body: {
      fontSize: 16,
      fontWeight: '400',
      color: '#121212',
      lineHeight: 24,
    } as TextStyle,

    caption: {
      fontSize: 12,
      fontWeight: '500',
      color: '#666666',
      textTransform: 'uppercase',
      letterSpacing: 1,
    } as TextStyle,

    card: {
      backgroundColor: '#FFFFFF',
      borderWidth: 2,
      borderColor: '#121212',
      borderRadius: 0,
      padding: 16,
      shadowColor: '#000',
      shadowOffset: { width: 8, height: 8 },
      shadowOpacity: 1,
      shadowRadius: 0,
      elevation: 4,
    } as ViewStyle,

    buttonPrimary: {
      backgroundColor: '#D02020',
      borderWidth: 2,
      borderColor: '#121212',
      paddingVertical: 14,
      paddingHorizontal: 20,
      borderRadius: 0,
      minHeight: 48,
      shadowColor: '#000',
      shadowOffset: { width: 4, height: 4 },
      shadowOpacity: 1,
      shadowRadius: 0,
      elevation: 2,
    } as ViewStyle,

    buttonSecondary: {
      backgroundColor: '#1040C0',
      borderWidth: 2,
      borderColor: '#121212',
      paddingVertical: 14,
      paddingHorizontal: 20,
      borderRadius: 0,
      minHeight: 48,
      shadowColor: '#000',
      shadowOffset: { width: 4, height: 4 },
      shadowOpacity: 1,
      shadowRadius: 0,
      elevation: 2,
    } as ViewStyle,

    buttonDanger: {
      backgroundColor: '#D02020',
      borderWidth: 2,
      borderColor: '#121212',
      paddingVertical: 14,
      paddingHorizontal: 20,
      borderRadius: 0,
      minHeight: 48,
      shadowColor: '#000',
      shadowOffset: { width: 4, height: 4 },
      shadowOpacity: 1,
      shadowRadius: 0,
      elevation: 2,
    } as ViewStyle,

    input: {
      backgroundColor: '#FFFFFF',
      borderWidth: 2,
      borderColor: '#121212',
      borderRadius: 0,
      paddingVertical: 12,
      paddingHorizontal: 16,
      fontSize: 16,
      color: '#121212',
    } as ViewStyle,
  },
};
